import { lazy, useEffect } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IconSizes } from '@pulse-web-ui/icons';
import { StateBlock } from '@pulse-web-ui/state-block';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { useDeleteProfileDraft, useGetProfile } from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  SUBMIT_EDIT_PROFILE,
  useStores,
} from '@shared/index';

const RGSDocumentFlatIcon = lazy(
  () => import('@pulse-web-ui/icons/lib/lazy/rgs-document-flat')
);

export const DraftStateBlock = observer(() => {
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const {
    MainStore: {
      agentContractStore: {
        updateProfile,
        updateAgentProfileDraft,
        setProfileAddendumStatus,
      },
    },
  } = useStores();

  const {
    isFetching: isDeleteDraftFetching,
    refetch,
    error,
  } = useDeleteProfileDraft();

  const {
    isFetching: isProfileFetching,
    refetch: getAgentProfile,
    error: profileError,
  } = useGetProfile();

  const isLoading = isDeleteDraftFetching || isProfileFetching;

  const handleSubmitDraftClick = () => {
    sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_CHECK);

    navigate(SUBMIT_EDIT_PROFILE);
  };

  const handleDeleteDraftClick = () => {
    sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_DELETEDRAFT);

    refetch().then(() =>
      getAgentProfile().then(({ data }) => {
        if (!data?.data) return;

        const { profile, agentProfileDraft, profileAddendumStatus } = data.data;

        updateProfile(profile);
        updateAgentProfileDraft(agentProfileDraft || {});
        setProfileAddendumStatus(profileAddendumStatus);
      })
    );
  };

  useEffect(() => {
    if (error || profileError) {
      showBoundary(BoundaryErrorType.SomethingWentWrong);
    }
  }, [error, profileError]);

  return (
    <StateBlock
      icon={<RGSDocumentFlatIcon size={IconSizes.large} />}
      title={t('AGENT_PROFILE:banners.draftTitle')}
      description={t('AGENT_PROFILE:banners.draftDescription')}
      submitButtonText={t('AGENT_PROFILE:banners.draftCheckAndSend')}
      cancelButtonText={t('AGENT_PROFILE:banners.deleteDraft')}
      onSubmit={handleSubmitDraftClick}
      onCancel={handleDeleteDraftClick}
      isLoading={isLoading}
      testId='agentProfileDraftBanner'
    />
  );
});
